import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import type { InboxMessage, InboxPage, InboxUnread } from '@capsico/contracts';
import { errorMessage, errorStatus, errorRef } from '../api/client';
import { fetchInbox, fetchUnread, markInboxRead } from '../api/notify';
import { appPathFor } from './deepLink';
import { showToast } from './useToast';

export const inboxKey = ['inbox'] as const;
export const unreadKey = ['inbox-unread'] as const;

/** The bell polls at most once a minute (pushes and the inbox page refresh it sooner). */
const UNREAD_POLL_MS = 60_000;

/** Unread count for the bell badge. Hidden (null) when signed out or the inbox is switched off. */
export function useUnreadCount(enabled: boolean): number | null {
  const query = useQuery({
    queryKey: unreadKey,
    queryFn: fetchUnread,
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? UNREAD_POLL_MS : false,
    retry: (count, error) => errorStatus(error) !== 404 && count < 1,
  });
  return enabled && query.data ? query.data.unread : null;
}

type Pages = InfiniteData<InboxPage, string | null>;

function patchRead(
  data: Pages | undefined,
  ids: Set<string> | 'all',
  at: string,
  unread: number,
): Pages | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      unread,
      rows: page.rows.map((row) =>
        row.readAt === null && (ids === 'all' || ids.has(row.id)) ? { ...row, readAt: at } : row,
      ),
    })),
  };
}

/**
 * The inbox (W18, system of record): keyset pages of messages, mark one read when opened, or all
 * read. Both are optimistic — the rows and the bell change at once and roll back if the server
 * says no. Opening a message returns the in-app screen its deep link points to.
 */
export function useInbox(enabled: boolean, messages: { failed: string }) {
  const client = useQueryClient();
  const query = useInfiniteQuery({
    queryKey: inboxKey,
    queryFn: ({ pageParam }) => fetchInbox(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    staleTime: 30_000,
  });

  const rows: InboxMessage[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.rows) ?? [],
    [query.data],
  );

  const read = useMutation({
    mutationFn: (ids: string[]) => markInboxRead(ids),
    onMutate: async (ids) => {
      await client.cancelQueries({ queryKey: inboxKey });
      await client.cancelQueries({ queryKey: unreadKey });
      const before = client.getQueryData<Pages>(inboxKey);
      const unreadBefore = client.getQueryData<InboxUnread>(unreadKey);
      const at = new Date().toISOString();
      const target = ids.length === 0 ? 'all' : new Set(ids);
      const unreadRows = (before?.pages ?? [])
        .flatMap((page) => page.rows)
        .filter((row) => row.readAt === null && (target === 'all' || target.has(row.id))).length;
      const known = unreadBefore?.unread ?? before?.pages[0]?.unread ?? 0;
      const next = target === 'all' ? 0 : Math.max(0, known - unreadRows);
      client.setQueryData<Pages>(inboxKey, (current) => patchRead(current, target, at, next));
      client.setQueryData<InboxUnread>(unreadKey, { unread: next });
      return { before, unreadBefore };
    },
    onError: (error, _ids, context) => {
      if (context?.before) client.setQueryData(inboxKey, context.before);
      if (context?.unreadBefore) client.setQueryData(unreadKey, context.unreadBefore);
      showToast(errorMessage(error, messages.failed), { tone: 'negative', ref: errorRef(error) });
    },
    onSuccess: (result) => {
      client.setQueryData<InboxUnread>(unreadKey, result);
      client.setQueryData<Pages>(inboxKey, (current) =>
        current
          ? { ...current, pages: current.pages.map((page) => ({ ...page, unread: result.unread })) }
          : current,
      );
    },
  });

  return {
    loading: query.isLoading,
    failed: query.isError && rows.length === 0,
    error: query.error ? errorMessage(query.error) : null,
    retry: () => void query.refetch(),
    rows,
    unread: query.data?.pages[0]?.unread ?? 0,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    markAllRead: () => read.mutate([]),
    markingAll: read.isPending && read.variables?.length === 0,
    /** Marks the message read (if it is not) and returns where its link leads, or null. */
    open: (message: InboxMessage): string | null => {
      if (message.readAt === null) read.mutate([message.id]);
      const link = message.webPath ?? message.deepLink;
      return link ? appPathFor(link) : null;
    },
  };
}
